import { useEffect, useMemo, useRef, useState } from "react";
import { Landmark, X } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { fetchConstituencies } from "@/lib/api";

export default function ConstituencySearch({ onSelect, initialValue = "", autofocus = false }) {
  const { lang, t } = useLang();
  const [q, setQ] = useState(initialValue);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    fetchConstituencies().then(setItems).catch(() => setItems([]));
  }, []);

  useEffect(() => {
    if (autofocus && inputRef.current) inputRef.current.focus();
  }, [autofocus]);

  useEffect(() => {
    function handleClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const nameField = lang === "ta" ? "tamil" : "english";

  // Filtered suggestions - match "as user types"
  const suggestions = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!items.length) return [];
    if (!query) {
      // When empty show all (as if dropdown), with "Choose Constituency" first
      return items;
    }
    // Match on both english and tamil regardless of selected language
    return items.filter((it) => {
      return (
        it.english.toLowerCase().includes(query) ||
        (it.tamil && it.tamil.toLowerCase().includes(query))
      );
    });
  }, [items, q]);

  function choose(item) {
    setQ(item[nameField]);
    setOpen(false);
    onSelect?.(item);
  }

  function onKey(e) {
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      if (active >= 0 && suggestions[active]) {
        e.preventDefault();
        choose(suggestions[active]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <div ref={wrapRef} className="relative" data-testid="constituency-search">
      <div
        className={`search-shadow bg-white rounded-[22px] border transition-colors ${
          open ? "border-indigo-400" : "border-transparent"
        } flex items-center gap-3 px-4 sm:px-5 py-3.5`}
      >
        <Landmark size={22} className="text-[#6b6894] shrink-0" />
        <input
          ref={inputRef}
          value={q}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
            setActive(-1);
          }}
          onKeyDown={onKey}
          placeholder={t("search_placeholder")}
          aria-label={t("search_your_constituency")}
          className={`w-full bg-transparent outline-none text-[16px] sm:text-[17px] text-[#1a1640] placeholder:text-[#a09dbf] ${
            lang === "ta" ? "font-tamil" : ""
          }`}
          data-testid="constituency-search-input"
        />
        {q ? (
          <button
            onClick={() => {
              setQ("");
              setActive(-1);
              setOpen(true);
              inputRef.current?.focus();
            }}
            className="shrink-0 rounded-full p-1.5 hover:bg-slate-100 text-[#6b6894]"
            aria-label={t("clear")}
            data-testid="constituency-search-clear"
          >
            <X size={16} />
          </button>
        ) : null}
      </div>

      {open && (
        <ul className="typeahead" data-testid="constituency-typeahead">
          {/* First entry: "Choose Constituency" — disabled header entry, unless user has typed */}
          {!q && (
            <li
              className="!cursor-default !bg-transparent"
              style={{ opacity: 0.6, fontStyle: "italic" }}
              data-testid="choose-constituency-placeholder"
            >
              <span className={lang === "ta" ? "font-tamil" : ""}>
                — {t("choose_constituency")} —
              </span>
            </li>
          )}

          {suggestions.length === 0 ? (
            <li className="!cursor-default !bg-transparent text-[#6b6894]">
              {lang === "ta" ? "பொருத்தம் இல்லை" : "No matches"}
            </li>
          ) : (
            suggestions.map((item, i) => (
              <li
                key={item.id}
                data-active={i === active}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  choose(item);
                }}
                data-testid={`suggestion-${item.english.replace(/\s+/g, "-")}`}
              >
                <span className={lang === "ta" ? "font-tamil" : ""}>
                  {item[nameField]}
                </span>
                {lang === "ta" ? (
                  <span className="hint">{item.english}</span>
                ) : (
                  <span className="hint font-tamil">{item.tamil}</span>
                )}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
